import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { useMemo, useState } from "react";
import { CalendarTeamEventCreateScreenQuery } from "./__generated__/CalendarTeamEventCreateScreenQuery.graphql";
import { CalendarTeamEventCreateScreenCreateCalendarEventMutation } from "./__generated__/CalendarTeamEventCreateScreenCreateCalendarEventMutation.graphql";
import { DateTime } from "luxon";
import { CalendarTeamEventEditComponent } from "./CalendarTeamEventEditComponent";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { CalendarTeamEventCreateScreenComponent_storeCalendars$key } from "./__generated__/CalendarTeamEventCreateScreenComponent_storeCalendars.graphql";

type Props = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "CalendarTeamEventCreateScreen">,
    UserLoggedInNavigatorNavigationProp<"CalendarTeamEventCreate">
  >;
  route: RouteProp<ParamList, "CalendarTeamEventCreateScreen">;
};

type ParamList = {
  CalendarTeamEventCreateScreen: { teamId: string; connections: string[] };
};

const Stack = createNativeStackNavigator<ParamList>();

export function InnerCalendarTeamEventCreateScreenComponent(props: {
  calendars: CalendarTeamEventCreateScreenComponent_storeCalendars$key;
  timezone: string;
  onClose: () => void;
  connections: string[];
}) {
  const calendars = useFragment(
    graphql`
      fragment CalendarTeamEventCreateScreenComponent_storeCalendars on StoreCalendar
      @relay(plural: true) {
        id
        isExternal
        ...CalendarTeamEventEditComponent_storeCalendars
      }
    `,
    props.calendars
  );

  const [title, setTitle] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const defaultStartAt = useMemo(() => {
    return DateTime.now().set({ minute: 0, second: 0, millisecond: 0 });
  }, []);
  const [startAt, setStartAt] = useState<DateTime>(defaultStartAt);
  const [endAt, setEndAt] = useState<DateTime>(
    defaultStartAt.plus({ hours: 1 })
  );
  const [calendarId, setCalendarId] = useState<string | null>(
    calendars.find((calendar) => !calendar.isExternal)?.id ?? null
  );

  const [createCalendarEvent, createCalendarEventLoading] =
    useMutation<CalendarTeamEventCreateScreenCreateCalendarEventMutation>(
      graphql`
        mutation CalendarTeamEventCreateScreenCreateCalendarEventMutation(
          $input: CreateCalendarEventInput!
          $connections: [ID!]!
        ) {
          createCalendarEvent(input: $input)
            @appendNode(
              connections: $connections
              edgeTypeName: "CalendarTeamCalendarEventsEdge"
            ) {
            id
            startAt {
              isoString
            }
            endAt {
              isoString
            }
            createdAt {
              isoString
            }
            isAllDayEvent
            ...CalendarTeamEventItem_event
          }
        }
      `
    );

  const disabled = useMemo(
    () => title.length === 0 || calendarId === null || startAt > endAt,
    [title.length, calendarId, startAt, endAt]
  );

  const intl_tokens = useIntlTokens();
  useNavigationHeader({
    type: "CREATE",
    onCancel: () => props.onClose(),
    onSave: () => {
      const startAtISO = startAt.toISO();
      const endAtISO = endAt.toISO();
      if (
        !startAtISO ||
        !endAtISO ||
        startAtISO >= endAtISO ||
        !calendarId ||
        title.length === 0
      )
        return;
      createCalendarEvent({
        variables: {
          connections: props.connections,
          input: {
            title,
            startAt: startAtISO,
            endAt: endAtISO,
            isAllDayEvent: isAllDay,
            calendarId: calendarId,
          },
        },
        onCompleted: () => {
          props.onClose();
        },
      });
    },
    saveLoading: createCalendarEventLoading,
    disabled,
    title: intl_tokens.AddCalendarEvent,
  });

  if (!calendarId) {
    return null;
  }

  return (
    <CalendarTeamEventEditComponent
      calendars={calendars}
      calendarId={calendarId}
      setCalendarId={setCalendarId}
      isAllDay={isAllDay}
      setIsAllDay={setIsAllDay}
      startAt={startAt}
      setStartAt={setStartAt}
      endAt={endAt}
      setEndAt={setEndAt}
      title={title}
      setTitle={setTitle}
      editable={true}
      timezone={props.timezone}
    />
  );
}

function CalendarTeamEventCreateScreenComponent(
  props: Props & { fetchKey: string }
) {
  const data = useLazyLoadQuery<CalendarTeamEventCreateScreenQuery>(
    graphql`
      query CalendarTeamEventCreateScreenQuery($teamId: ID!) {
        store {
          timezone
          team(id: $teamId) {
            calendars(includeExternal: true) {
              ...CalendarTeamEventCreateScreenComponent_storeCalendars
            }
          }
        }
      }
    `,
    { teamId: props.route.params.teamId },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.store.team) {
    return null;
  }

  return (
    <InnerCalendarTeamEventCreateScreenComponent
      onClose={() => props.navigation.goBack()}
      timezone={data.store.timezone}
      calendars={data.store.team.calendars}
      connections={props.route.params.connections}
    />
  );
}

function CalendarTeamEventCreateScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <CalendarTeamEventCreateScreenComponent {...props} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const CalendarTeamEventCreate = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"CalendarTeamEventCreate">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    const intl_tokens = useIntlTokens();
    return (
      <Stack.Navigator initialRouteName="CalendarTeamEventCreateScreen">
        <Stack.Screen
          name="CalendarTeamEventCreateScreen"
          component={CalendarTeamEventCreateScreen}
          initialParams={{
            teamId: props.route.params.teamId,
            connections: props.route.params.connections,
          }}
          options={navigationHeaderOptions({
            type: "CANCEL",
            onClose: () => props.navigation.goBack(),
            title: intl_tokens.AddCalendarEvent,
          })}
        />
      </Stack.Navigator>
    );
  }
);
